import React, { useState } from 'react';
import { Table, Button, Modal, Form, Input, Select, DatePicker, message, Space, Tag, Row, Col, Card, Statistic } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, DollarOutlined, EyeOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../utils/api';
import dayjs from 'dayjs';

const { Option } = Select;

const OnlineStudents = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [editingStudent, setEditingStudent] = useState(null);
    const [form] = Form.useForm();
    const queryClient = useQueryClient();

    const [filterStatus, setFilterStatus] = useState(null);
    const [searchText, setSearchText] = useState('');

    // Fetch students
    const { data: studentsData, isLoading } = useQuery('online-students', async () => {
        const response = await api.get('/online-students', {
            params: { limit: 10000 }
        });
        return response.data.data;
    });

    // Create student mutation
    const createMutation = useMutation(
        (values) => api.post('/online-students', values),
        {
            onSuccess: () => {
                message.success(t('onlineAccounting.studentCreated'));
                setIsModalVisible(false);
                form.resetFields();
                queryClient.invalidateQueries('online-students');
            },
            onError: (error) => {
                message.error(error.response?.data?.message || t('onlineAccounting.error'));
            }
        }
    );

    // Update student mutation
    const updateMutation = useMutation(
        ({ id, values }) => api.put(`/online-students/${id}`, values),
        {
            onSuccess: () => {
                message.success(t('onlineAccounting.studentUpdated'));
                setIsModalVisible(false);
                setEditingStudent(null);
                form.resetFields();
                queryClient.invalidateQueries('online-students');
            },
            onError: (error) => {
                message.error(error.response?.data?.message || t('onlineAccounting.error'));
            }
        }
    );

    // Delete student mutation
    const deleteMutation = useMutation(
        (id) => api.delete(`/online-students/${id}`),
        {
            onSuccess: () => {
                message.success(t('onlineAccounting.studentDeleted'));
                queryClient.invalidateQueries('online-students');
            },
            onError: (error) => {
                message.error(error.response?.data?.message || t('onlineAccounting.error'));
            }
        }
    );

    const handleSubmit = (values) => {
        const formattedValues = {
            ...values,
            dateOfBirth: values.dateOfBirth ? values.dateOfBirth.toISOString() : null,
            enrollmentDate: values.enrollmentDate ? values.enrollmentDate.toISOString() : null,
        };

        if (editingStudent) {
            updateMutation.mutate({ id: editingStudent._id, values: formattedValues });
        } else {
            createMutation.mutate(formattedValues);
        }
    };

    const handleEdit = (student) => {
        setEditingStudent(student);
        form.setFieldsValue({
            ...student,
            dateOfBirth: student.dateOfBirth ? dayjs(student.dateOfBirth) : null,
            enrollmentDate: student.enrollmentDate ? dayjs(student.enrollmentDate) : null,
        });
        setIsModalVisible(true);
    };

    const handleDelete = (id) => {
        Modal.confirm({
            title: t('onlineAccounting.confirmDelete'),
            content: t('onlineAccounting.confirmDeleteStudent'),
            onOk: () => deleteMutation.mutate(id),
        });
    };

    const getPaymentStatusTag = (student) => {
        const { totalFees, paidAmount } = student.paymentDetails || {};
        if (paidAmount >= totalFees) {
            return <Tag color="green">{t('onlineAccounting.paid')}</Tag>;
        } else if (paidAmount > 0) {
            return <Tag color="orange">{t('onlineAccounting.partial')}</Tag>;
        }
        return <Tag color="red">{t('onlineAccounting.unpaid')}</Tag>;
    };

    const columns = [
        {
            title: t('onlineAccounting.studentId'),
            dataIndex: 'studentId',
            key: 'studentId',
        },
        {
            title: t('onlineAccounting.name'),
            key: 'name',
            render: (_, record) => `${record.firstName} ${record.lastName}`,
        },
        {
            title: t('onlineAccounting.email'),
            dataIndex: 'email',
            key: 'email',
        },
        {
            title: t('onlineAccounting.phone'),
            dataIndex: 'phone',
            key: 'phone',
        },
        {
            title: t('onlineAccounting.course'),
            dataIndex: 'courseName',
            key: 'courseName',
        },
        {
            title: t('onlineAccounting.totalFees'),
            key: 'totalFees',
            render: (_, record) => {
                const { totalFees, currency } = record.paymentDetails || {};
                return `${currency === 'IQD' ? 'IQD' : '$'}${totalFees?.toLocaleString() || '0'}`;
            },
        },
        {
            title: t('onlineAccounting.remaining'),
            key: 'remaining',
            render: (_, record) => {
                const { remainingBalance, currency } = record.paymentDetails || {};
                return `${currency === 'IQD' ? 'IQD' : '$'}${remainingBalance?.toLocaleString() || '0'}`;
            },
        },
        {
            title: t('onlineAccounting.paymentStatus'),
            key: 'paymentStatus',
            render: (_, record) => getPaymentStatusTag(record),
        },
        {
            title: t('onlineAccounting.status'),
            dataIndex: 'status',
            key: 'status',
            render: (status) => {
                const color = status === 'active' ? 'green' : status === 'inactive' ? 'orange' : 'red';
                return <Tag color={color}>{t(`onlineAccounting.${status}`)}</Tag>;
            },
        },
        {
            title: t('onlineAccounting.actions'),
            key: 'actions',
            render: (_, record) => (
                <Space>
                    <Button type="link" icon={<EyeOutlined />} onClick={() => navigate(`/online-students/${record._id}`)}>
                        {t('onlineAccounting.view')}
                    </Button>
                    <Button type="link" icon={<EditOutlined />} onClick={() => handleEdit(record)} />
                    <Button type="link" danger icon={<DeleteOutlined />} onClick={() => handleDelete(record._id)} />
                </Space>
            ),
        },
    ];

    const filteredData = studentsData?.filter((student) => {
        if (filterStatus && student.status !== filterStatus) return false;
        if (searchText) {
            const search = searchText.toLowerCase();
            return (
                student.firstName?.toLowerCase().includes(search) ||
                student.lastName?.toLowerCase().includes(search) ||
                student.email?.toLowerCase().includes(search) ||
                student.studentId?.toLowerCase().includes(search) ||
                student.courseName?.toLowerCase().includes(search)
            );
        }
        return true;
    });

    // Calculate statistics
    const totalStudents = filteredData?.length || 0;
    const activeStudents = filteredData?.filter(s => s.status === 'active').length || 0;

    return (
        <div>
            <h1>{t('onlineAccounting.students')}</h1>

            <Row gutter={16} style={{ marginBottom: 16 }}>
                <Col span={8}>
                    <Card>
                        <Statistic title={t('onlineAccounting.totalStudents')} value={totalStudents} />
                    </Card>
                </Col>
                <Col span={8}>
                    <Card>
                        <Statistic title={t('onlineAccounting.activeStudents')} value={activeStudents} valueStyle={{ color: '#3f8600' }} />
                    </Card>
                </Col>
            </Row>

            <Space style={{ marginBottom: 16 }}>
                <Input.Search
                    placeholder={t('onlineAccounting.searchStudent')}
                    allowClear
                    onSearch={setSearchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    style={{ width: 300 }}
                />
                <Select
                    placeholder={t('onlineAccounting.filterByStatus')}
                    allowClear
                    onChange={setFilterStatus}
                    style={{ width: 150 }}
                >
                    <Option value="active">{t('onlineAccounting.active')}</Option>
                    <Option value="inactive">{t('onlineAccounting.inactive')}</Option>
                    <Option value="suspended">{t('onlineAccounting.suspended')}</Option>
                    <Option value="graduated">{t('onlineAccounting.graduated')}</Option>
                </Select>
                <Button type="primary" icon={<PlusOutlined />} onClick={() => setIsModalVisible(true)}>
                    {t('onlineAccounting.addStudent')}
                </Button>
            </Space>

            <Table
                columns={columns}
                dataSource={filteredData}
                rowKey="_id"
                loading={isLoading}
                pagination={{ pageSize: 10 }}
            />

            <Modal
                title={editingStudent ? t('onlineAccounting.editStudent') : t('onlineAccounting.addStudent')}
                open={isModalVisible}
                onCancel={() => {
                    setIsModalVisible(false);
                    setEditingStudent(null);
                    form.resetFields();
                }}
                footer={null}
                width={800}
            >
                <Form form={form} layout="vertical" onFinish={handleSubmit}>
                    <Row gutter={16}>
                        <Col span={24}>
                            <Form.Item name="status" label={t('onlineAccounting.status')} initialValue="active">
                                <Select>
                                    <Option value="active">{t('onlineAccounting.active')}</Option>
                                    <Option value="inactive">{t('onlineAccounting.inactive')}</Option>
                                    <Option value="suspended">{t('onlineAccounting.suspended')}</Option>
                                    <Option value="graduated">{t('onlineAccounting.graduated')}</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item name="firstName" label={t('onlineAccounting.firstName')} rules={[{ required: true }]}>
                                <Input />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="lastName" label={t('onlineAccounting.lastName')}>
                                <Input />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item name="email" label={t('onlineAccounting.email')}>
                                <Input />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="phone" label={t('onlineAccounting.phone')} rules={[{ required: true }]}>
                                <Input />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item name="dateOfBirth" label={t('onlineAccounting.dateOfBirth')}>
                                <DatePicker style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="gender" label={t('onlineAccounting.gender')}>
                                <Select>
                                    <Option value="male">{t('onlineAccounting.male')}</Option>
                                    <Option value="female">{t('onlineAccounting.female')}</Option>
                                    <Option value="other">{t('onlineAccounting.other')}</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item name="courseName" label={t('onlineAccounting.course')} rules={[{ required: true }]}>
                        <Input />
                    </Form.Item>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item name={['paymentDetails', 'totalFees']} label={t('onlineAccounting.totalFees')} rules={[{ required: true }]}>
                                <Input type="number" min={0} />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name={['paymentDetails', 'currency']} label={t('onlineAccounting.currency')} initialValue="USD">
                                <Select>
                                    <Option value="USD">USD</Option>
                                    <Option value="IQD">IQD</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item name="notes" label={t('onlineAccounting.notes')}>
                        <Input.TextArea rows={3} />
                    </Form.Item>

                    <Space>
                        <Button type="primary" htmlType="submit" loading={createMutation.isLoading || updateMutation.isLoading}>
                            {editingStudent ? t('onlineAccounting.update') : t('onlineAccounting.create')}
                        </Button>
                        <Button onClick={() => {
                            setIsModalVisible(false);
                            setEditingStudent(null);
                            form.resetFields();
                        }}>
                            {t('onlineAccounting.cancel')}
                        </Button>
                    </Space>
                </Form>
            </Modal>
        </div>
    );
};

export default OnlineStudents;
